import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Button } from '@treezor/design-system';

import { dashboardUserStatus, TDashboardUserStatus } from '@/features/dashboard-users/store/support-user.model';
import { DateText } from '@/ui-kit/Date/DateText';
import { openModal } from '@/store/actions/core.actions';
import { IAccessExpirationModalProps } from '@/features/dashboard-users/components/DashboardUserStatus/accessExpirationModal';
import {
  AccessExpirationActions,
  TAccessExpirationAction,
} from '@/features/dashboard-users/store/types/accessExpiration.type';

interface IDashboardUserStatusDetailsProps {
  status: TDashboardUserStatus;
  accessExpirationDate?: Date;
  userId: string;
}

export function DashboardUserStatusDetails({ status, accessExpirationDate, userId }: IDashboardUserStatusDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleRenew = useCallback(
    (action: TAccessExpirationAction) => () => {
      dispatch(
        openModal<IAccessExpirationModalProps>('accessExpirationModal', {
          userId,
          action,
        }),
      );
    },
    [dispatch, userId],
  );

  let info = accessExpirationDate ? null : t('pages.administration.supportsUsers.table.status.info.permanent');
  let expiration = t('pages.administration.supportsUsers.table.expiration.expiresOn');
  let width = 186;
  let actions = null;
  if (!accessExpirationDate) {
    actions = (
      <Button variant="ghost" size="s" color="inverse" onClick={handleRenew(AccessExpirationActions.ACTIVATE)}>
        {t('pages.administration.supportsUsers.table.expiration.activate')}
      </Button>
    );
    width = 315;
  }

  if (status === dashboardUserStatus.BLOCKED) {
    info = t(`pages.administration.supportsUsers.table.status.info.${status}`);
    expiration = null;
    actions = null;
    width = 300;
  } else if (status === dashboardUserStatus.EXPIRED) {
    info = null;
    expiration = t('pages.administration.supportsUsers.table.expiration.expired');
    actions = (
      <Button variant="ghost" size="s" color="inverse" onClick={handleRenew(AccessExpirationActions.RENEW)}>
        {t('pages.administration.supportsUsers.table.expiration.renew')}
      </Button>
    );
    width = 187;
  } else if (status === dashboardUserStatus.NEAR_EXPIRED) {
    info = t(`pages.administration.supportsUsers.table.status.info.${status}`);
    expiration = t('pages.administration.supportsUsers.table.expiration.expiresOn');
    actions = (
      <Button variant="ghost" size="s" color="inverse" onClick={handleRenew(AccessExpirationActions.RENEW)}>
        {t('pages.administration.supportsUsers.table.expiration.renew')}
      </Button>
    );
    width = 287;
  }

  return (
    <div className="flex flex-col gap-4" style={{ width: `${width}px` }}>
      {info && <span className="typo-body-regular-small">{info}</span>}
      {expiration && (
        <span className="typo-body-regular-small">
          <span>{expiration} </span>
          <DateText date={accessExpirationDate} />
        </span>
      )}
      {actions && <div className="flex justify-start items-start">{actions}</div>}
    </div>
  );
}
